import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { ArticleCTA } from "@/components/site/ArticleCTA";
import {
  ResourceCard,
  resourceHref,
  type EditorialResource,
} from "@/components/resources/ResourceEditorialPrimitives";
import { publicResourcesQuery } from "@/lib/query-registry";
import { formatUpdated } from "@/lib/article-utils";
import type { Locale } from "@/lib/i18n";
import { quotePath, quoteSearch } from "@/lib/quote-links";
import {
  resourceMatchesCluster,
  resolveResourceCommercialContext,
  type ResourceCluster,
} from "@/lib/resource-commerce";
import { isSpanishTranslationMissing } from "@/lib/resource-translation";

export type ClusterKey = Exclude<ResourceCluster, "development" | "general">;

type EditorialLink = {
  href: string;
  title: string;
  description: string;
  label: string;
};

type EditorialManifest = {
  pillar: EditorialLink;
  recommended: EditorialLink[];
};

const EDITORIAL: Record<ClusterKey, Record<Locale, EditorialManifest>> = {
  performance: {
    en: {
      pillar: {
        href: "/resources/shopify-performance-optimization-guide",
        title: "The Shopify Performance Optimization Guide",
        description: "A technical guide to Core Web Vitals, rendering, app contention, measurement and safe implementation.",
        label: "Start here",
      },
      recommended: [
        {
          href: "/resources/shopify-app-bloat-performance",
          title: "How Shopify app bloat damages storefront performance",
          description: "Understand how duplicate scripts, app embeds and third-party work affect the buying journey.",
          label: "Root cause",
        },
        {
          href: "/resources/sitewide-lcp-regression-36-urls",
          title: "A sitewide LCP regression affecting 36 commercial URLs",
          description: "A field note on diagnosing a shared performance failure without inventing a result before field data updates.",
          label: "Field evidence",
        },
      ],
    },
    es: {
      pillar: {
        href: "/es/recursos/guia-optimizacion-rendimiento-shopify",
        title: "Guía de Optimización de Rendimiento Shopify",
        description: "Guía técnica de Core Web Vitals, render, conflictos de apps, medición e implementación segura.",
        label: "Empieza aquí",
      },
      recommended: [
        {
          href: "/es/recursos/bloat-apps-shopify-rendimiento",
          title: "Cómo el bloat de apps perjudica el rendimiento Shopify",
          description: "Scripts duplicados, app embeds y trabajo de terceros que afectan el journey de compra.",
          label: "Causa raíz",
        },
      ],
    },
  },
  migration: {
    en: {
      pillar: {
        href: "/resources/shopify-migration-guide-2026",
        title: "The Shopify Migration Guide: Platform-Neutral Planning",
        description: "The broad framework for moving from WooCommerce, Magento, BigCommerce, PrestaShop or a custom platform to Shopify without assuming Shopify Plus.",
        label: "Start here",
      },
      recommended: [
        {
          href: "/resources/shopify-plus-migration",
          title: "Shopify Plus Migration: Costs, Timeline & Process",
          description: "Use the Plus-specific guide when B2B, checkout, Markets, multi-store governance or enterprise integrations justify the tier.",
          label: "Shopify Plus path",
        },
        {
          href: "/resources/woocommerce-to-shopify-migration",
          title: "WooCommerce to Shopify migration",
          description: "Translate plugins, WordPress content, catalog data, customer accounts and URL patterns into a supported Shopify operation.",
          label: "Source platform",
        },
        {
          href: "/resources/magento-to-shopify-migration-what-agencies-get-wrong",
          title: "Magento to Shopify migration",
          description: "Plan configurable products, customer groups, multi-store behavior, extensions, integrations and operational cutover.",
          label: "Source platform",
        },
        {
          href: "/resources/bigcommerce-to-shopify-migration-guide",
          title: "BigCommerce to Shopify migration",
          description: "Map categories, options, customer groups, Stencil behavior, apps, redirects and integration parity.",
          label: "Source platform",
        },
        {
          href: "/resources/shopify-replatforming-playbook",
          title: "The Shopify Replatforming Playbook",
          description: "Cross-platform workstreams, data, SEO continuity, governance, cutover control and stabilization for teams moving to Shopify or Shopify Plus.",
          label: "Execution playbook",
        },
        {
          href: "/resources/llantas-cavazos-migration-case-study",
          title: "Llantas Cavazos Shopify migration case study",
          description: "Review documented delivery evidence, controls and limitations from a catalog-heavy migration.",
          label: "Case evidence",
        },
      ],
    },
    es: {
      pillar: {
        href: "/es/recursos/shopify-migration-guide-2026",
        title: "Guía de Migración a Shopify: planeación neutral",
        description: "El framework general para migrar desde WooCommerce, Magento, BigCommerce, PrestaShop o una plataforma custom sin asumir Shopify Plus.",
        label: "Empieza aquí",
      },
      recommended: [
        {
          href: "/es/recursos/shopify-plus-migration",
          title: "Migración a Shopify Plus: costos, tiempos y proceso",
          description: "Usa la guía específica de Plus cuando B2B, checkout, Markets, gobierno multi-store o integraciones enterprise justifican el tier.",
          label: "Ruta Shopify Plus",
        },
        {
          href: "/es/recursos/migracion-woocommerce-shopify",
          title: "Migración de WooCommerce a Shopify",
          description: "Traduce plugins, contenido WordPress, catálogo, cuentas de cliente y URLs a una operación soportada en Shopify.",
          label: "Plataforma de origen",
        },
        {
          href: "/es/recursos/magento-to-shopify-migration-what-agencies-get-wrong",
          title: "Migración de Magento a Shopify",
          description: "Planea productos configurables, customer groups, multi-store, extensiones, integraciones y cutover operativo.",
          label: "Plataforma de origen",
        },
        {
          href: "/es/recursos/bigcommerce-to-shopify-migration-guide",
          title: "Migración de BigCommerce a Shopify",
          description: "Mapea categorías, opciones, customer groups, Stencil, apps, redirects y paridad de integraciones.",
          label: "Plataforma de origen",
        },
        {
          href: "/es/recursos/playbook-replatforming-shopify",
          title: "Playbook de Replatforming a Shopify",
          description: "Workstreams, datos, continuidad SEO, gobierno, control de cutover y estabilización para equipos que migran a Shopify o Shopify Plus.",
          label: "Playbook de ejecución",
        },
        {
          href: "/es/recursos/caso-llantas-cavazos-migracion",
          title: "Caso de migración de Llantas Cavazos a Shopify",
          description: "Revisa evidencia documentada, controles y límites de una migración con catálogo amplio.",
          label: "Evidencia del caso",
        },
      ],
    },
  },
  conversion: {
    en: {
      pillar: {
        href: "/resources/shopify-conversion-rate-optimization-playbook",
        title: "The Shopify Conversion Rate Optimization Playbook",
        description: "A structured approach to PDP, cart, checkout, mobile UX, measurement and decision rules.",
        label: "Start here",
      },
      recommended: [],
    },
    es: {
      pillar: {
        href: "/es/recursos/playbook-cro-shopify",
        title: "Playbook de CRO para Shopify",
        description: "Un enfoque estructurado para PDP, carrito, checkout, UX móvil, medición y reglas de decisión.",
        label: "Empieza aquí",
      },
      recommended: [],
    },
  },
  "technical-seo": {
    en: {
      pillar: {
        href: "/resources/shopify-technical-seo-playbook",
        title: "Shopify Technical SEO: Crawling, Indexation and Architecture",
        description: "A practical framework for URL ownership, canonicals, schema, rendering and migration continuity.",
        label: "Start here",
      },
      recommended: [],
    },
    es: {
      pillar: {
        href: "/es/recursos/shopify-technical-seo-playbook",
        title: "SEO técnico Shopify: crawling, indexación y arquitectura",
        description: "Framework práctico de ownership de URLs, canonicals, schema, rendering y continuidad en migraciones.",
        label: "Empieza aquí",
      },
      recommended: [],
    },
  },
};

export interface HubCopy {
  eyebrow: string;
  title: string;
  intro: string;
  serviceLabel: string;
  serviceHref: string;
  articlesTitle: string;
  articlesEmpty: string;
  read: string;
  min: string;
  by: string;
  updated: string;
  backLabel: string;
  backHref: string;
}

const ACCENT: Record<ClusterKey, { section: string; text: string; border: string }> = {
  performance: {
    section: "bg-[rgba(15,118,110,0.05)]",
    text: "text-[#1f1f1f] dark:text-[#ccff00]",
    border: "border-[rgba(15,118,110,0.22)]",
  },
  conversion: {
    section: "bg-[rgba(161,107,31,0.05)]",
    text: "text-[#1f1f1f] dark:text-[#ccff00]",
    border: "border-[rgba(161,107,31,0.22)]",
  },
  migration: {
    section: "bg-[rgba(91,122,82,0.06)]",
    text: "text-[#55754d] dark:text-[#a7d39b]",
    border: "border-[rgba(91,122,82,0.24)]",
  },
  "technical-seo": {
    section: "bg-[rgba(79,70,229,0.05)]",
    text: "text-[#0a0a0a] dark:text-[#ccff00]",
    border: "border-[rgba(79,70,229,0.22)]",
  },
};

function fallbackMatchesCluster(
  resource: EditorialResource & Record<string, any>,
  cluster: ClusterKey,
  locale: Locale,
): boolean {
  const tags = locale === "es" && resource.tags_es?.length ? resource.tags_es : resource.tags_en;
  const haystack = [
    resource.slug,
    resource.slug_es,
    resource.title_en,
    resource.title_es,
    resource.excerpt_en,
    resource.excerpt_es,
    ...(tags ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (cluster === "performance") return /performance|rendimiento|speed|velocidad|core web vitals|\blcp\b|\binp\b|\bcls\b|app bloat/.test(haystack);
  if (cluster === "migration") return /migration|migraci[oó]n|replatform|woocommerce|magento|bigcommerce|prestashop|shopify plus upgrade/.test(haystack);
  if (cluster === "technical-seo") return /technical seo|seo t[eé]cnico|crawl|indexation|indexaci[oó]n|canonical|schema|structured data|hreflang|rendering/.test(haystack);
  return /\bcro\b|conversion|conversi[oó]n|checkout|product page|\bpdp\b|cart|carrito|merchandising/.test(haystack);
}

export function clusterFor(
  resource: EditorialResource & Record<string, any>,
  locale: Locale,
): ClusterKey | null {
  const order: ClusterKey[] = ["performance", "migration", "technical-seo", "conversion"];
  for (const cluster of order) {
    if (resourceMatchesCluster(resource, cluster, locale) || fallbackMatchesCluster(resource, cluster, locale)) {
      return cluster;
    }
  }
  return null;
}

export function ResourceHub({
  locale,
  cluster,
  copy,
}: {
  locale: Locale;
  cluster: ClusterKey;
  copy: HubCopy;
}) {
  // The route loader primes this query during SSR. useQuery deliberately does
  // not throw to the route error boundary if a client transition or background
  // refresh fails; the static editorial paths remain useful without the grid.
  const { data } = useQuery(publicResourcesQuery());
  const all = ((data?.resources as EditorialResource[] | undefined) ?? []);
  const isEs = locale === "es";
  const manifest = EDITORIAL[cluster][locale];
  const slug =
    cluster === "performance"
      ? isEs
        ? "rendimiento"
        : "performance"
      : cluster === "migration"
        ? isEs
          ? "migracion"
          : "migration"
        : cluster === "technical-seo"
          ? isEs
            ? "seo-tecnico"
            : "technical-seo"
          : "conversion";
  const pathname = `${isEs ? "/es/recursos" : "/resources"}/${slug}`;
  const context = resolveResourceCommercialContext({ locale, pathname, resource: { cluster } });
  const accent = ACCENT[cluster];
  const editorialHrefs = new Set([
    manifest.pillar.href,
    ...manifest.recommended.map((item) => item.href),
  ]);
  const dynamicArticles = all.filter((resource: EditorialResource & Record<string, any>) => {
    if (!(resourceMatchesCluster(resource, cluster, locale) || fallbackMatchesCluster(resource, cluster, locale))) {
      return false;
    }
    if (editorialHrefs.has(resourceHref(resource, locale))) return false;
    if (isEs && isSpanishTranslationMissing(resource)) return false;
    return true;
  });

  const labels = isEs
    ? {
        start: "Empieza aquí",
        situations: "Rutas recomendadas",
        situationsTitle: "Elige la decisión que estás intentando tomar.",
        library: "Recursos del tema",
        all: "Explorar todos los recursos",
        service: "Servicio relacionado",
        fixed: "Alcance y precio fijo para lo aprobado.",
        empty: "La selección editorial seguirá visible mientras se publican más recursos.",
      }
    : {
        start: "Start here",
        situations: "Recommended paths",
        situationsTitle: "Choose the decision you are trying to make.",
        library: "Resources in this topic",
        all: "Browse all resources",
        service: "Related service",
        fixed: "Fixed scope and price for the approved work.",
        empty: "The editorial selection remains available while more resources are published.",
      };

  const recommendedGrid =
    manifest.recommended.length >= 4
      ? "md:grid-cols-2 xl:grid-cols-4"
      : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <article className="pb-28">
      <section className="resource-hub-hero relative overflow-hidden" data-surface="dark">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 lg:grid-cols-[1fr_0.58fr] lg:items-end lg:py-28">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${accent.text}`}>
              <Link to={copy.backHref} className="hover:underline">{copy.backLabel}</Link>
              <span aria-hidden className="mx-2">/</span>
              {copy.eyebrow}
            </p>
            <h1 className="mt-6 max-w-5xl text-balance font-display text-5xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-muted-foreground">{copy.intro}</p>
          </div>
          <div className={`premium-panel rounded-3xl border ${accent.border} bg-background/75 p-7`}>
            <p className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${accent.text}`}>{labels.start}</p>
            <h2 className="mt-4 font-display text-2xl font-semibold leading-tight text-foreground">{manifest.pillar.title}</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{manifest.pillar.description}</p>
            <a href={manifest.pillar.href} className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background">
              {copy.read}<ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <Reveal>
          <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${accent.text}`}>{labels.situations}</p>
          <h2 className="mt-5 max-w-4xl font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">{labels.situationsTitle}</h2>
        </Reveal>
        {manifest.recommended.length ? (
          <div className={`mt-12 grid gap-px overflow-hidden rounded-3xl border border-border bg-border ${recommendedGrid}`}>
            {manifest.recommended.map((item, index) => (
              <Reveal key={item.href} delay={index * 40}>
                <a href={item.href} className="group flex h-full flex-col bg-background p-8">
                  <span className={`font-mono text-xs ${accent.text}`}>0{index + 1}</span>
                  <p className={`mt-6 text-[10px] font-semibold uppercase tracking-[0.2em] ${accent.text}`}>{item.label}</p>
                  <h3 className="mt-4 font-display text-2xl font-semibold leading-tight text-foreground">{item.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{item.description}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-7 text-sm font-semibold text-foreground">
                    {copy.read}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground">{labels.empty}</p>
        )}
      </section>

      <section className="evidence-section" data-surface="light">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
          <Reveal>
            <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${accent.text}`}>{labels.library}</p>
            <h2 className="mt-5 font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">{copy.articlesTitle}</h2>
          </Reveal>
          {dynamicArticles.length ? (
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {dynamicArticles.map((resource, index) => (
                <Reveal key={resource.id ?? resource.slug} delay={(index % 3) * 40}>
                  <ResourceCard
                    resource={resource}
                    locale={locale}
                    updated={resource.reviewed_at || resource.updated_at ? formatUpdated((resource.reviewed_at ?? resource.updated_at) as string, locale) : ""}
                    readLabel={copy.read}
                  />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <p className="text-sm text-muted-foreground">{copy.articlesEmpty}</p>
              <Link to={isEs ? "/es/recursos" : "/resources"} className="text-sm font-semibold text-foreground underline-offset-4 hover:underline">
                {labels.all} →
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <Reveal>
          <div className={`grid gap-8 rounded-3xl border ${accent.border} ${accent.section} p-8 sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center`}>
            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${accent.text}`}>{labels.service}</p>
              <h2 className="mt-4 font-display text-3xl font-semibold text-foreground">{context.serviceLabel}</h2>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">{labels.fixed}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to={context.serviceHref} className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-2.5 text-sm font-semibold text-foreground">
                {copy.serviceLabel}<ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={quotePath(locale)}
                search={quoteSearch({ source: pathname, intent: context.intent, challenge: context.challenge })}
                className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background"
              >
                {context.offerLabel}<ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24">
        <Reveal>
          <ArticleCTA variant="footer" cluster={cluster} source={pathname} accent className="mt-0" />
        </Reveal>
      </section>
    </article>
  );
}

export function collectionPageJsonLd(
  name: string,
  url: string,
  description: string,
  itemUrls: string[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    url,
    description,
    hasPart: itemUrls.map((itemUrl) => ({ "@type": "Article", url: itemUrl })),
  };
}
